// MARK://dhk8/admin-shell/components/AdminLayout
// The ONE admin shell. Topbar (brand + impersonation banner + user + theme) over an
// icon rail (left) + content. Same HIG-green geometry as the POS PosLayout — all
// measurements from styles/tokens.css (--pos-*), touch targets ≥44pt, no edge strips.
// Phone breakpoint (≤520px): rail → hamburger drawer (scrim + left slide-over).
//
// The nav list is filtered by role: "Cuentas de terceros" (superadmin) only shows for
// the superadmin role.

import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutGrid, ChefHat, Users, Printer, FileText, BarChart3, Boxes, Building2,
  LogOut, Menu as MenuIcon, X, ShieldAlert, ExternalLink, SlidersHorizontal, UserCheck,
  Truck, ShoppingCart, BookOpen, QrCode, CalendarClock, NotebookText, Heart,
  Package, Network, Clock, Warehouse, ShieldCheck, Carrot, Stamp, ScrollText, Ruler,
  CreditCard, Layers, ArrowLeftRight,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { useAuthStore } from '../stores/auth-store.js'
import { useTenantStore } from '../stores/tenant-store.js'
import { logImpersonation } from '../lib/clients/impersonate.js'
import { t } from '../i18n/es-MX.js'
import BrandMark from './BrandMark.js'
import ThemeSwitcher from './ThemeSwitcher.js'
import AvatarMenu from './AvatarMenu.js'
import { moduleForRoute, isAlwaysAllowedRoute } from '@dockethub/contracts/shared'

interface NavItem { path: string; label: string; Icon: LucideIcon; role?: string; group?: string }

const POS_APP_URL = 'https://pos.nintaigo.com'

// Árbol jerárquico por dominios. El orden es group-contiguo: el render agrupa por
// `group` (separadores en el rail de 64px, encabezados de sección en el drawer).
// Orden = ciclo de vida canónico: Compras → Inventario/Lotes → Recetario →
// Catálogo → Operación → Clientes → Fiscal → Reportes → Administración → Ajustes.
const NAV_ITEMS: readonly NavItem[] = [
  { path: '/dashboard', label: t('nav.dashboard'), Icon: LayoutGrid, group: '' },
  // Compras / Abasto (entra al ciclo)
  { path: '/suppliers', label: t('nav.suppliers'), Icon: Truck, group: 'Compras' },
  { path: '/purchase-orders', label: t('nav.purchaseOrders'), Icon: ShoppingCart, group: 'Compras' },
  // Inventario (lo comprado, organizado por lote y almacén)
  { path: '/inventory/warehouses', label: 'Almacenes', Icon: Warehouse, group: 'Inventario' },
  { path: '/inventory', label: t('nav.inventory'), Icon: Boxes, group: 'Inventario' },
  { path: '/inventory/lots', label: t('nav.lots'), Icon: Layers, group: 'Inventario' },
  { path: '/inventory/movements', label: t('nav.movements'), Icon: ArrowLeftRight, group: 'Inventario' },
  // Recetario (con qué insumo hago qué producto)
  { path: '/ingredients', label: t('nav.ingredients'), Icon: Carrot, group: 'Recetario' },
  { path: '/recipes', label: t('nav.recipes'), Icon: NotebookText, group: 'Recetario' },
  // Catálogo (lo que ofrezco/vendo)
  { path: '/catalog', label: t('nav.catalog'), Icon: ChefHat, group: 'Catálogo' },
  { path: '/catalog/modifiers', label: t('nav.modifiers'), Icon: SlidersHorizontal, group: 'Catálogo' },
  { path: '/combos', label: t('nav.combos'), Icon: Package, group: 'Catálogo' },
  { path: '/menus', label: t('nav.menus'), Icon: BookOpen, group: 'Catálogo' },
  { path: '/catalog/units', label: t('nav.units'), Icon: Ruler, group: 'Catálogo' },
  // Operación / Piso
  { path: '/floor', label: t('nav.floor'), Icon: LayoutGrid, group: 'Operación' },
  { path: '/floor/qr-codes', label: t('nav.qrCodes'), Icon: QrCode, group: 'Operación' },
  { path: '/reservations', label: t('nav.reservations'), Icon: CalendarClock, group: 'Operación' },
  { path: '/waitlist', label: t('nav.waitlist'), Icon: Clock, group: 'Operación' },
  // Clientes
  { path: '/customers/crm', label: t('nav.customersCrm'), Icon: Heart, group: 'Clientes' },
  { path: '/customers/fiscal', label: t('nav.customersFiscal'), Icon: FileText, group: 'Clientes' },
  // Fiscal
  { path: '/fiscal', label: t('nav.fiscal'), Icon: Stamp, group: 'Fiscal' },
  { path: '/invoices', label: t('nav.invoices'), Icon: FileText, group: 'Fiscal' },
  // Reportes
  { path: '/reports', label: t('nav.reports'), Icon: BarChart3, group: 'Reportes' },
  // Administración
  { path: '/users', label: t('nav.users'), Icon: Users, group: 'Administración' },
  { path: '/roles', label: 'Roles', Icon: ShieldCheck, group: 'Administración' },
  { path: '/audit', label: t('nav.audit'), Icon: ScrollText, group: 'Administración' },
  { path: '/devices', label: t('nav.devices'), Icon: Printer, group: 'Administración' },
  { path: '/organizations', label: t('nav.organizations'), Icon: Network, role: 'superadmin', group: 'Administración' },
  { path: '/impersonate', label: t('nav.impersonate'), Icon: UserCheck, role: 'owner', group: 'Administración' },
  { path: '/superadmin', label: t('nav.superadmin'), Icon: Building2, role: 'superadmin', group: 'Administración' },
  // Ajustes
  { path: '/settings/payments', label: 'Pagos', Icon: CreditCard, group: 'Ajustes' },
]

// Módulos visibles desde el claim `modules` del JWT (#3 F3). Igual criterio que el
// POS: decodificamos el token solo para gating de UI (el backend enforcea de verdad)
// y memorizamos por token. Sin el claim (sesión vieja) → undefined → nav legacy
// (cero regresión; los owner/manager ven todo como hoy).
let _modTok: string | null = null
let _modVal: readonly string[] | undefined
function jwtModules(token: string | null | undefined): readonly string[] | undefined {
  const tok = token ?? null
  if (tok === _modTok) return _modVal
  _modTok = tok
  _modVal = undefined
  if (tok) {
    try {
      const part = tok.split('.')[1]
      if (part) {
        const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { modules?: unknown }
        if (Array.isArray(payload.modules)) _modVal = payload.modules.filter((x): x is string => typeof x === 'string')
      }
    } catch { /* token malformado → undefined → nav legacy */ }
  }
  return _modVal
}

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const session = useAuthStore((s) => s.session)
  const logout = useAuthStore((s) => s.logout)
  const stopImpersonating = useAuthStore((s) => s.stopImpersonating)
  const loadAccount = useAuthStore((s) => s.loadAccount)
  const tenant = useTenantStore((s) => s.tenant)
  const loadTenant = useTenantStore((s) => s.load)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const env = session?.activeEnv ?? 'test'
  const hasLiveLink = !!session?.liveTenantId && !!session?.testTenantId && !session?.impersonating

  function handleLogout() { setDrawerOpen(false); logout(); navigate('/login', { replace: true }) }
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])
  useEffect(() => { void loadAccount().then(() => loadTenant()) }, [loadAccount, loadTenant, session?.userId])
  useEffect(() => { void loadTenant() }, [loadTenant, session?.tenantId])
  useEffect(() => {
    if (!drawerOpen) return
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setDrawerOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  const roles = session?.roles ?? []
  const modules = jwtModules(session?.token)
  const visibleNav = NAV_ITEMS.filter((i) => {
    // Rutas de plataforma (superadmin/owner): gate por rol, como antes.
    if (i.role) return roles.includes(i.role)
    // Siempre visibles (dashboard / núcleo).
    if (isAlwaysAllowedRoute('admin', i.path)) return true
    // Gate por MÓDULO cuando el JWT trae el claim (#3 F3 — cierra el hueco de
    // que 22/25 módulos se vieran para todos); legacy (mostrar) si no hay claim.
    if (modules !== undefined) {
      const mod = moduleForRoute('admin', i.path)
      return mod === undefined ? true : modules.includes(mod)
    }
    return true
  })

  // Agrupa la nav visible por dominio (orden group-contiguo) → separadores en el
  // rail (64px) y encabezados de sección en el drawer.
  const navGroups: { group: string; items: typeof visibleNav }[] = []
  for (const it of visibleNav) {
    const g = it.group ?? ''
    const last = navGroups[navGroups.length - 1]
    if (last && last.group === g) last.items.push(it)
    else navGroups.push({ group: g, items: [it] })
  }

  // Active = the longest nav path that prefixes the current URL — so
  // /floor/qr-codes highlights "QR de mesa", not "Plano", and /recipes/:id
  // highlights "Recetario". Ties broken toward the more specific route.
  const activePath = visibleNav
    .filter((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? null

  return (
    <div className="pos-shell">
      <header className="pos-topbar">
        <div className="pos-topbar__brand">
          <button type="button" className="pos-topbar__burger" aria-label="Abrir navegación" aria-expanded={drawerOpen} onClick={() => setDrawerOpen(true)}>
            <MenuIcon size={22} aria-hidden />
          </button>
          <BrandMark size="md" />
          <span className="pos-topbar__tag">{t('app.tag')}</span>
        </div>
        <div className="pos-topbar__center" style={{ gap: 8 }}>
          {/* El toggle Pruebas/Vivo vive ahora en el AvatarMenu (paridad POS). Aquí
              solo queda el indicador estático para cuentas sin live-link. */}
          {!hasLiveLink && tenant && (
            <span className={`adm-badge ${tenant.mode === 'LIVE' ? 'adm-badge--ok' : 'adm-badge--warn'}`}>{tenant.mode === 'LIVE' ? 'EN VIVO' : 'PRUEBAS'}</span>
          )}
          {session?.impersonating && (
            <span className="adm-badge adm-badge--warn" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <ShieldAlert size={13} aria-hidden /> Suplantando: {session.impersonating.name}
              <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ minHeight: 26, padding: '0 8px' }} onClick={() => {
                // For user-kind impersonations, leave an audit trail. Tenant-kind
                // already logs separately in dhk8-platform-tenants when the
                // superadmin enters; here we keep it best-effort.
                const imp = session.impersonating
                if (imp?.kind === 'user' && imp.previous) {
                  void logImpersonation({
                    event: 'IMPERSONATION_END',
                    tenantId: imp.tenantId,
                    actorUserId: imp.previous.userId,
                    targetUserId: imp.userId ?? '',
                    targetName: imp.name,
                  })
                }
                stopImpersonating()
              }}>Salir</button>
            </span>
          )}
        </div>
        <div className="pos-topbar__meta">
          <ThemeSwitcher />
          {/* Nombre+dominio sueltos → consolidados en el avatar (paridad POS). */}
          <AvatarMenu onLogout={handleLogout} />
        </div>
      </header>

      <div className="pos-body">
        <nav className="pos-rail" aria-label="Navegación principal">
          {navGroups.flatMap((g, gi) => [
            ...(gi > 0 ? [<div key={`sep-${g.group || gi}`} className="pos-rail__sep" aria-hidden />] : []),
            ...g.items.map(({ path, label, Icon }) => {
              const active = activePath === path
              return (
                <Link key={path} to={path} className={`pos-rail__item${active ? ' pos-rail__item--active' : ''}`} aria-current={active ? 'page' : undefined}>
                  <Icon size={20} aria-hidden />
                  <span className="pos-rail__label">{label}</span>
                </Link>
              )
            }),
          ])}
          <div className="pos-rail__spacer" />
          <a href={POS_APP_URL} target="_blank" rel="noopener noreferrer" className="pos-rail__item" title="Ir al POS">
            <ExternalLink size={20} aria-hidden />
            <span className="pos-rail__label">POS</span>
          </a>
          <button type="button" className="pos-rail__item pos-rail__item--logout" onClick={handleLogout}>
            <LogOut size={20} aria-hidden />
            <span className="pos-rail__label">{t('nav.logout')}</span>
          </button>
        </nav>
        <main className="pos-content">
          {env === 'test' && !session?.impersonating && (
            <div className="adm-env-banner" role="status">
              <strong>ENTORNO DE PRUEBAS</strong> — estás viendo el dataset de pruebas. Ajusta lo que quieras sin preocuparte; las ventas no cuentan. Cambia a “En vivo” cuando estés lista para operar.
            </div>
          )}
          {children}
        </main>
      </div>

      <div className={`pos-drawer${drawerOpen ? ' pos-drawer--open' : ''}`} aria-hidden={!drawerOpen}>
        <button type="button" className="pos-drawer__scrim" aria-label="Cerrar navegación" tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)} />
        <nav className="pos-drawer__panel" aria-label="Navegación principal">
          <div className="pos-drawer__head">
            <BrandMark size="md" />
            <button type="button" className="pos-drawer__close" aria-label="Cerrar navegación" tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)}>
              <X size={20} aria-hidden />
            </button>
          </div>
          <div className="pos-drawer__list">
            {navGroups.flatMap((g) => [
              ...(g.group ? [<div key={`sec-${g.group}`} className="pos-drawer__section">{g.group}</div>] : []),
              ...g.items.map(({ path, label, Icon }) => {
                const active = activePath === path
                return (
                  <Link key={path} to={path} className={`pos-drawer__item${active ? ' pos-drawer__item--active' : ''}`} aria-current={active ? 'page' : undefined} tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)}>
                    <Icon size={20} aria-hidden />
                    <span>{label}</span>
                  </Link>
                )
              }),
            ])}
          </div>
          <div className="pos-drawer__foot">
            <a href={POS_APP_URL} target="_blank" rel="noopener noreferrer" className="pos-drawer__item" tabIndex={drawerOpen ? 0 : -1} onClick={() => setDrawerOpen(false)}>
              <ExternalLink size={20} aria-hidden />
              <span>Ir al POS</span>
            </a>
            <button type="button" className="pos-drawer__item pos-drawer__item--logout" tabIndex={drawerOpen ? 0 : -1} onClick={handleLogout}>
              <LogOut size={20} aria-hidden />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </nav>
      </div>

      <style>{shellStyles}</style>
    </div>
  )
}

// Same shell CSS as PosLayout (HIG-green; all values from styles/tokens.css).
const shellStyles = `
  .pos-shell { position: relative; display: flex; flex-direction: column; height: 100dvh; width: 100%; background: var(--pos-canvas); color: var(--pos-fg); font-family: var(--font-sans); overflow: hidden; }
  .pos-topbar { flex-shrink: 0; height: var(--pos-topbar-h, 56px); display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: var(--pos-space-3); padding: 0 var(--pos-space-4); background: var(--pos-surface); border-bottom: 1px solid var(--pos-separator); box-shadow: var(--pos-shadow-1); z-index: var(--pos-z-topbar, 30); }
  .pos-topbar__brand { display: flex; align-items: center; gap: var(--pos-space-2); min-width: 0; }
  .pos-topbar__burger { display: none; align-items: center; justify-content: center; width: 44px; height: 44px; margin-left: calc(var(--pos-space-2) * -1); border: none; border-radius: var(--pos-radius-md); background: transparent; color: var(--pos-fg-secondary); cursor: pointer; transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out); }
  .pos-topbar__burger:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-topbar__burger:active { transform: scale(.95); }
  .pos-topbar__tag { font-family: var(--font-display, var(--font-sans)); font-size: var(--pos-text-xxs); letter-spacing: var(--pos-tracking-widest); text-transform: uppercase; color: var(--pos-fg-tertiary); white-space: nowrap; }
  .pos-topbar__center { display: flex; justify-content: center; align-items: center; min-width: 0; }
  .pos-topbar__meta { display: flex; flex-direction: row; align-items: center; gap: var(--pos-space-2); justify-self: end; min-width: 0; }
  .pos-topbar__id { display: flex; flex-direction: column; align-items: flex-end; min-width: 0; line-height: var(--pos-leading-tight); }
  .pos-topbar__user { font-size: var(--pos-text-xs); font-weight: var(--pos-weight-medium); color: var(--pos-fg-secondary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 14ch; }
  .pos-topbar__domain { font-size: var(--pos-text-xxs); color: var(--pos-fg-tertiary); white-space: nowrap; }
  .pos-body { display: flex; flex: 1; min-height: 0; }
  .pos-rail { flex-shrink: 0; width: var(--pos-sidebar-w, 64px); display: flex; flex-direction: column; align-items: center; gap: var(--pos-space-1); padding: var(--pos-space-2) var(--pos-space-1); background: var(--pos-surface); border-right: 1px solid var(--pos-separator); overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; z-index: var(--pos-z-rail, 20); scrollbar-width: thin; scrollbar-color: var(--pos-separator) transparent; }
  .pos-rail::-webkit-scrollbar { width: 5px; }
  .pos-rail::-webkit-scrollbar-thumb { background: var(--pos-separator); border-radius: 3px; }
  .pos-rail::-webkit-scrollbar-track { background: transparent; }
  .pos-rail__item { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; width: 60px; min-height: 52px; padding: var(--pos-space-2) 2px; border: none; border-radius: var(--pos-radius-md); background: transparent; color: var(--pos-fg-tertiary); font-family: var(--font-display, var(--font-sans)); font-size: var(--pos-text-xxs); font-weight: var(--pos-weight-medium); letter-spacing: var(--pos-tracking-snug); text-decoration: none; cursor: pointer; transition: color var(--pos-duration-fast) var(--pos-ease-out), background var(--pos-duration-fast) var(--pos-ease-out); }
  .pos-rail__label { line-height: 1; text-align: center; }
  .pos-rail__item:hover { color: var(--pos-fg); background: var(--pos-elevated); }
  .pos-rail__item:active { transform: scale(.97); }
  .pos-rail__item--active { color: var(--pos-tint-on); background: var(--pos-tint-fill); }
  .pos-rail__item--active:hover { background: var(--pos-tint-fill-hover); }
  .pos-rail__spacer { flex: 1; }
  .pos-rail__item--logout { color: var(--pos-fg-tertiary); }
  .pos-rail__item--logout:hover { color: var(--pos-danger); background: var(--pos-elevated); }
  .pos-rail__sep { width: 26px; height: 1px; flex-shrink: 0; background: var(--pos-separator); margin: var(--pos-space-1) 0; }
  .pos-drawer__section { padding: var(--pos-space-3) var(--pos-space-4) var(--pos-space-1); font-family: var(--font-display, var(--font-sans)); font-size: var(--pos-text-xxs); font-weight: var(--pos-weight-semibold, 600); letter-spacing: var(--pos-tracking-widest); text-transform: uppercase; color: var(--pos-fg-tertiary); }
  .pos-content { flex: 1; min-width: 0; min-height: 0; padding: var(--pos-content-pad, 1.25rem); overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
  .pos-drawer { display: none; }
  .pos-drawer__scrim { position: fixed; inset: 0; border: none; padding: 0; margin: 0; background: var(--pos-overlay); cursor: pointer; opacity: 0; pointer-events: none; transition: opacity var(--pos-duration-base) var(--pos-ease-in-out); z-index: var(--pos-z-drawer-scrim, 50); }
  .pos-drawer__panel { position: fixed; top: 0; left: 0; bottom: 0; width: min(82vw, 300px); display: flex; flex-direction: column; background: var(--pos-surface); border-right: 1px solid var(--pos-separator); box-shadow: var(--pos-shadow-4, 0 24px 60px -16px rgba(0,0,0,.55)); transform: translateX(-100%); transition: transform var(--pos-duration-base) var(--pos-ease-out); z-index: var(--pos-z-drawer, 60); }
  .pos-drawer--open .pos-drawer__scrim { opacity: 1; pointer-events: auto; }
  .pos-drawer--open .pos-drawer__panel { transform: translateX(0); }
  .pos-drawer__head { flex-shrink: 0; display: flex; align-items: center; justify-content: space-between; gap: var(--pos-space-2); height: var(--pos-topbar-h, 56px); padding: 0 var(--pos-space-3) 0 var(--pos-space-4); border-bottom: 1px solid var(--pos-separator); }
  .pos-drawer__close { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border: none; border-radius: var(--pos-radius-md); background: transparent; color: var(--pos-fg-secondary); cursor: pointer; transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out); }
  .pos-drawer__close:hover { background: var(--pos-elevated); color: var(--pos-fg); }
  .pos-drawer__list { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; display: flex; flex-direction: column; gap: 2px; padding: var(--pos-space-2); }
  .pos-drawer__foot { flex-shrink: 0; padding: var(--pos-space-2); border-top: 1px solid var(--pos-separator); }
  .pos-drawer__item { display: flex; align-items: center; gap: var(--pos-space-3); min-height: 44px; padding: 0 var(--pos-space-3); border: none; border-radius: var(--pos-radius-md); background: transparent; color: var(--pos-fg-secondary); font-family: var(--font-display, var(--font-sans)); font-size: var(--pos-text-sm); font-weight: var(--pos-weight-medium); text-decoration: none; text-align: left; width: 100%; cursor: pointer; transition: color var(--pos-duration-fast) var(--pos-ease-out), background var(--pos-duration-fast) var(--pos-ease-out); }
  .pos-drawer__item:hover { color: var(--pos-fg); background: var(--pos-elevated); }
  .pos-drawer__item:active { transform: scale(.99); }
  .pos-drawer__item--active { color: var(--pos-tint-on); background: var(--pos-tint-fill); }
  .pos-drawer__item--active:hover { background: var(--pos-tint-fill-hover); }
  .pos-drawer__item--logout { color: var(--pos-fg-tertiary); }
  .pos-drawer__item--logout:hover { color: var(--pos-danger); background: var(--pos-elevated); }
  /* Stripe-style env switcher (segmented control). */
  .adm-env-switch { display: inline-flex; padding: 2px; border-radius: var(--pos-radius-pill); background: var(--pos-elevated); border: 1px solid var(--pos-separator); }
  .adm-env-switch__opt { border: none; background: transparent; cursor: pointer; min-height: 30px; padding: 0 12px; border-radius: var(--pos-radius-pill); font-family: var(--font-display, var(--font-sans)); font-size: var(--pos-text-xs); font-weight: var(--pos-weight-semibold); color: var(--pos-fg-tertiary); transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out); }
  .adm-env-switch__opt--active.adm-env-switch__opt--test { background: var(--pos-warning-bg); color: var(--pos-warning); }
  .adm-env-switch__opt--active.adm-env-switch__opt--live { background: var(--pos-success-bg); color: var(--pos-success); }
  /* Persistent "ENTORNO DE PRUEBAS" banner above page content. */
  .adm-env-banner { margin-bottom: var(--pos-space-4); padding: var(--pos-space-2) var(--pos-space-4); border-radius: var(--pos-radius-md); background: var(--pos-warning-bg); color: var(--pos-warning); font-size: var(--pos-text-sm); line-height: var(--pos-leading-snug, 1.35); }
  @media (max-width: 520px) {
    .pos-rail { display: none; }
    .pos-topbar__burger { display: inline-flex; }
    .pos-drawer { display: block; }
    .pos-topbar__tag { display: none; }
    .pos-topbar__domain { display: none; }
    /* Grid 3-col no encoge ni envuelve → se encimaba. En móvil pasamos a flex
       (como el PosLayout): marca y meta no encogen, el centro fluye. */
    .pos-topbar { display: flex; padding: 0 var(--pos-space-3); gap: var(--pos-space-2); }
    .pos-topbar__brand { flex-shrink: 0; }
    .pos-topbar__center { flex: 1 1 auto; min-width: 0; overflow: hidden; }
    .pos-topbar__meta { flex-shrink: 0; }
    /* El nombre (hasta 14ch) es el que empujaba al overlap — fuera en móvil. */
    .pos-topbar__user { display: none; }
    .adm-env-switch { flex-shrink: 0; }
  }
  @media (prefers-reduced-motion: reduce) { .pos-drawer__panel, .pos-drawer__scrim { transition: none; } }
`
